import { MAX_PARTY_POSITIONS, inMinimapVision, type EntityName, type MinimapSpot } from "odyssey-shared";

/**
 * Where a party member's mark stands on the minimap and the Map's Nearby field (P2.12-T35, ADR-100's note of
 * 2026-10-28): a member the page draws is marked where it is drawn, the smoothed position its white dot used to take, so
 * the mark follows them as closely as any other player's. Snapshot interest reaches past vision (`VISION_MARGIN_TILES`
 * less the hysteresis), so a member inside vision is always drawn. A member who leaves the snapshot keeps the position
 * they were last drawn at, outside vision, until a `partyPositions` frame moves them, so the mark never jumps back to a
 * feed position up to one interval old; the low-rate feed (decision 14) places only a member the page has not drawn
 * since then, who stands outside vision, for the compass.
 */

/**
 * A member's seat as the drawn characters are matched against it: roster place, first and family name.
 */
export interface PartySeat {
  readonly place: number;
  readonly first: string;
  readonly family: string;
}

/**
 * The drawn positions of the party's members, in tiles, by roster place, with the handle each was drawn as;
 * preallocated. `unnamed` counts the players drawn whose name the page does not hold yet, any of whom may be a member.
 */
export interface LiveParty {
  count: number;
  unnamed: number;
  readonly place: Int32Array;
  readonly handle: Int32Array;
  readonly x: Float64Array;
  readonly y: Float64Array;
}

/**
 * A buffer for as many members as a `partyPositions` frame holds.
 *
 * @returns The empty buffer.
 */
export function createLiveParty(): LiveParty {
  return { count: 0, unnamed: 0, place: new Int32Array(MAX_PARTY_POSITIONS), handle: new Int32Array(MAX_PARTY_POSITIONS), x: new Float64Array(MAX_PARTY_POSITIONS), y: new Float64Array(MAX_PARTY_POSITIONS) };
}

/**
 * Empties the buffer for the next frame.
 *
 * @param live The buffer.
 */
export function resetLiveParty(live: LiveParty): void {
  live.count = 0;
  live.unnamed = 0;
}

/**
 * The index of a place in the buffer.
 *
 * @param live The buffer.
 * @param place The roster place.
 * @returns The index, or -1.
 */
function liveIndexOf(live: LiveParty, place: number): number {
  for (let i = 0; i < live.count; i += 1) {
    if (live.place[i] === place) {
      return i;
    }
  }
  return -1;
}

/**
 * Adds a drawn member. A place already held keeps the entity of the higher handle, the newer one, so a reconnect's
 * lingering old entity never moves the mark; one past the buffer's capacity is dropped.
 *
 * @param live The buffer.
 * @param place The member's roster place.
 * @param handle The handle the member is drawn as.
 * @param x Tiles across, as drawn.
 * @param y Tiles down, as drawn.
 */
export function addLiveParty(live: LiveParty, place: number, handle: number, x: number, y: number): void {
  let at = liveIndexOf(live, place);
  if (at >= 0 && (live.handle[at] as number) > handle) {
    return;
  }
  if (at < 0) {
    at = live.count;
    if (at >= live.place.length) {
      return;
    }
    live.count = at + 1;
  }
  live.place[at] = place;
  live.handle[at] = handle;
  live.x[at] = x;
  live.y[at] = y;
}

/**
 * Makes `seen` a copy of `live`, as a `partyPositions` frame that moves a member does: the members still drawn keep
 * their drawn positions, and the ones remembered from before the frame are forgotten in favour of the frame's.
 *
 * @param seen The positions remembered.
 * @param live The members drawn this frame.
 */
export function copyLiveParty(seen: LiveParty, live: LiveParty): void {
  seen.count = 0;
  seen.unnamed = live.unnamed;
  for (let i = 0; i < live.count; i += 1) {
    addLiveParty(seen, live.place[i] as number, live.handle[i] as number, live.x[i] as number, live.y[i] as number);
  }
}

/**
 * Remembers this frame's drawn members: each one's position replaces the one remembered for its place, and a member
 * no longer drawn keeps the last. The count of unnamed players is this frame's.
 *
 * @param seen The positions remembered.
 * @param live The members drawn this frame.
 */
export function rememberLiveParty(seen: LiveParty, live: LiveParty): void {
  seen.unnamed = live.unnamed;
  for (let i = 0; i < live.count; i += 1) {
    const place = live.place[i] as number;
    const at = liveIndexOf(seen, place);
    if (at < 0) {
      addLiveParty(seen, place, live.handle[i] as number, live.x[i] as number, live.y[i] as number);
    } else {
      seen.handle[at] = live.handle[i] as number;
      seen.x[at] = live.x[i] as number;
      seen.y[at] = live.y[i] as number;
    }
  }
}

/**
 * The roster place of the member a drawn character is, by first and family name, which are unique to a character.
 *
 * @param name The drawn character's name.
 * @param seats The members on the player's map, the player excluded.
 * @returns The place, or -1 when the character is no member or its name is not held yet.
 */
export function partyPlaceOf(name: EntityName, seats: readonly PartySeat[]): number {
  if (name.first === "") {
    return -1;
  }
  for (const seat of seats) {
    if (seat.first === name.first && seat.family === name.family) {
      return seat.place;
    }
  }
  return -1;
}

/**
 * Where a member's mark stands: at the position remembered when the page has drawn them, else at the feed's, except
 * that a feed position inside vision while a player of unknown name is drawn gives no mark, since that player may be
 * the member and already has a dot.
 *
 * @param spot The member as the feed placed them.
 * @param seen The positions remembered.
 * @param self The player's position in tiles.
 * @returns The index in `seen` of the drawn position, -1 for the feed's, or -2 for no mark.
 */
export function partySpotSource(spot: MinimapSpot, seen: LiveParty, self: { readonly x: number; readonly y: number }): number {
  const at = liveIndexOf(seen, spot.place);
  if (at >= 0) {
    return at;
  }
  return seen.unnamed > 0 && inMinimapVision(spot.x - self.x, spot.y - self.y) ? -2 : -1;
}
